import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types';
import { detectMobileOrMiniProgram } from '../utils/supplierView';
import { resolveDefaultRouteByRole } from '../utils/workbenchView';
import { buildLoginPathWithReturnTo } from '../utils/loginReturnTo';
import { resolveEffectiveUserRole } from '../utils/authSession';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  loginPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  loginPath = '/login',
}) => {
  const { isAuthenticated, user, authInitialized } = useAuthStore();
  const preferMobileLayout = detectMobileOrMiniProgram();
  const location = useLocation();

  if (!authInitialized) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const returnTarget = `${location.pathname}${location.search}`;
    return <Navigate to={buildLoginPathWithReturnTo(returnTarget, loginPath)} replace />;
  }

  const onSetPasswordPage = location.pathname === '/account/set-password';
  if (user.must_change_password && !onSetPasswordPage) {
    return <Navigate to="/account/set-password" replace />;
  }
  if (!user.must_change_password && onSetPasswordPage) {
    return (
      <Navigate
        to={resolveDefaultRouteByRole(
          (resolveEffectiveUserRole(user) || user.role) as UserRole,
          preferMobileLayout,
        )}
        replace
      />
    );
  }

  const roleForAccess = (resolveEffectiveUserRole(user) || user.role) as UserRole;
  if (allowedRoles && !allowedRoles.includes(roleForAccess)) {
    return (
      <Navigate
        to={resolveDefaultRouteByRole(roleForAccess, preferMobileLayout)}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
